import React from "react";
import { history } from "../../store";
import PropTypes from "prop-types";
import { defineMessages, injectIntl } from "react-intl";
import { connect } from "react-redux";
import { withFormik } from "formik";
import { withRouter } from "react-router-dom";
import { compose } from "recompose";
import * as Yup from "yup";
import { completeInvitationThunk } from "../../modules/session/sessionActions";
import { route as homeRoute } from "./../HomeContainer/HomeContainer";
import { HTTP_INTERNAL_SERVER_ERROR } from "../../api/apiClient";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { withStyles } from "@mui/styles";
import { fetchCurrentUserThunk } from "../../modules/user/userActions";

const messages = defineMessages({
  headline: {
    id: "AcceptInvite.completeInvite",
    defaultMessage: "Einladung abschließen",
  },
  subheadline: {
    id: "AcceptInvite.infotext",
    defaultMessage:
      "Nach der Passwortvergabe bitte ausloggen. Ihre Emailadresse ist gleichzeitig Ihr Benutzername.",
  },
  sendPassword: {
    id: "AcceptInvite.sendPassword",
    defaultMessage: "Passwort senden",
  },
  inputLabel: {
    id: "AcceptInvite.passwordTextLabel",
    defaultMessage: "Passwort",
  },
  submitError: {
    id: "AcceptInvite.submitError",
    defaultMessage: "Es ist etwas schiefgegangen. Versuche es später erneut",
  },
});

const styles = (theme) => ({
  layout: {
    paddingTop: theme.spacing(8),
    width: "auto",
    display: "block", // Fix IE11 ticket.
    marginLeft: theme.spacing(3),
    marginRight: theme.spacing(3),
    [theme.breakpoints.up(400 + theme.spacing(6))]: {
      width: 400,
      marginLeft: "auto",
      marginRight: "auto",
    },
  },

  avatar: {
    margin: theme.spacing(),
    backgroundColor: theme.palette.secondary.main,
  },
  form: {
    width: "100%", // Fix IE11 ticket.
    marginTop: theme.spacing(),
  },
  submit: {
    marginTop: theme.spacing(3),
  },
  error: {
    color: theme.palette.error,
  },
});

const passwordValidationScheme = Yup.object().shape({
  password: Yup.string().required(),
});

// Wrap our form with the using withFormik HoC
const formControl = {
  // Transform outer props into form values
  mapPropsToValues: (props) => ({
    password: "",
  }),
  // Add a custom validation function (this can be async too!)
  validationSchema: passwordValidationScheme,
  // Submission handler
  handleSubmit: async (
    values,
    { props, setSubmitting, setErrors, resetForm },
  ) => {
    const { password } = values;
    const { token } = props.match.params;
    const { formatMessage } = props.intl;
    const dispatch = props.dispatch;

    try {
      await dispatch(completeInvitationThunk({ token, password })).unwrap();
      await dispatch(fetchCurrentUserThunk()).unwrap();
      resetForm();
      setSubmitting(false);
      history.push(homeRoute);
    } catch (err) {
      let submitErrorMessage = {};
      if (err.code === HTTP_INTERNAL_SERVER_ERROR) {
        submitErrorMessage = formatMessage(messages.submitError);
        setErrors({ submitErrorMessage });
      }
    }
  },
};

const AcceptInviteForm = (props) => {
  const {
    classes,
    values,
    handleChange,
    handleSubmit,
    touched,
    errors,
    intl: { formatMessage },
  } = props;
  return (
    <React.Fragment>
      <Box
        component="main"
        sx={(theme) => ({
          pt: theme.spacing(6),
          width: "auto",
          display: "block",
          mx: theme.spacing(3),

          [theme.breakpoints.up(400)]: {
            maxWidth: 400,
            ml: "auto",
            mr: "auto",
          },
        })}
      >
        <Paper
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            pt: 2,
            px: 3,
            pb: 3,
          }}
        >
          <Typography variant="h5">
            {formatMessage(messages.headline)}
          </Typography>
          <Typography variant="subheading" align="center">
            {formatMessage(messages.subheadline)}
          </Typography>
          <form className={classes.form} onSubmit={handleSubmit}>
            <FormControl margin="normal" required fullWidth>
              <TextField
                label={"Passwort"}
                margin="normal"
                required
                fullWidth
                type="password"
                error={touched.password && Boolean(errors.password)}
                id="password"
                name="password"
                value={values.password}
                onChange={handleChange}
                autoComplete="password"
                autoFocus
              />
            </FormControl>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              className={classes.submit}
            >
              {formatMessage(messages.sendPassword)}
            </Button>
            {errors.submitError && (
              <Typography variant="subtitle1">
                {formatMessage(messages.submitError)}
              </Typography>
            )}
          </form>
        </Paper>
      </Box>
    </React.Fragment>
  );
};

AcceptInviteForm.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default compose(
  connect(),
  injectIntl,
  withStyles(styles),
  withRouter,
  withFormik(formControl),
)(AcceptInviteForm);
